<template lang="pug">
.consultation-details.neutral300.fullscreen
  template(v-if="isFetching")
    v-container.pa-4(fluid)
      v-row(no-gutters)
        v-col
          v-row
            v-col(cols="1")
              v-card.round-xl(outlined link width="36" height="36" @click="onClickBack")
                div.d-flex.align-center.justify-center.fill-height
                  v-icon(color="primary") mdi-arrow-left
            v-col
              v-skeleton-loader(type="article@5")
        v-col
          v-skeleton-loader(type="article@5")
  template(v-else)
    div.neutral300
      v-row.pa-4(no-gutters)
        //- left panel
        v-col(cols="7" style="padding-right: 6px;")
          div.d-flex.align-center.mb-2
            //- go back
            v-card.round-xl(outlined link @click="checkClickBack" width="36" height="36")
              div.d-flex.align-center.justify-center.fill-height
                v-icon(color="primary") mdi-arrow-left
            //- dr name
            v-menu(v-if="isDoctor" offset-y right rounded="lg" min-width="200")
              template(v-slot:activator="{ on }")
                v-card.round-xl.ml-4.px-3(outlined height="36" v-on="on")
                  div.d-flex.align-center.fill-height
                    div.text-sm.font-medium {{ member.name }}
                    v-avatar.ml-2(size="20" color="neutral300")
                      template(v-if="member.profile_image")
                        v-img(:src="`https://img.yezza.io/50x50/${member.profile_image.path}`")
                      v-icon(v-else color="neutral500" size="15") $yzi-user-03
              v-list.text-sm.py-0
                v-list-item
                  .py-3
                    .text-sm.font-semibold {{ member.user.first_name }}
                    .text-sm.neutral500--text {{ getMemberRole(member.roles) }}
                v-divider.basic300
                v-list-item(@click="redirectTo('/admin/clinic/settings/account')")
                  v-icon.mr-2(color="primary" size=16) $yzi-user-01
                  .text-sm.font-medium {{ $t('account_settings') }}
                v-list-item(v-if="Auth('page', '/admin/clinic/settings')" @click="redirectTo('/admin/clinic/settings')")
                  v-icon.mr-2(color="primary" size=16) $yzi-medical-square
                  .text-sm.font-medium {{ $t('clinic_management') }}
                v-divider.basic300
                v-list-item(:href="logoutUrl()")
                  v-icon.mr-2(color="primary" size=16) $yzi-log-out-01
                  .text-sm.font-medium {{ $t('log_out') }}
            v-spacer
            //- num of patient
            waiting-dropdown
            //- serving time
            template(v-if="isDoctor && registration")
              //- waiting & on hold
              template(v-if="registration.status === 'waiting' || registration.status === 'on_hold'")
                v-card.round-xl.ml-4.pl-3.pr-1(outlined height="36")
                  div.d-flex.align-center.fill-height
                    div.text-sm.font-bold.danger500--text.mr-3(v-if="registration.urgent") Urgent
                    div.text-sm.font-medium.neutral700--text {{ `${getStatus(registration)}: ${getWaitingDuration(registration)}` }}
                    v-menu(offset-y left bottom rounded="lg" attach)
                      template(v-slot:activator="{ on }")
                        div(v-on="on")
                          y-button.ml-3(label="Call in" :loading="isLoadingCallPatientIn" :disabled="isLoadingStartConsultation" size="xxs" height="24" unset-min-width button-class="px-2")
                      v-list.py-0
                        template(v-if="seats.length > 0")
                          template(v-for="(seat, seatIdx) in seats")
                            v-divider(v-if="seats !== 0")
                            v-list-item(@click="onCallPatientIn(seat)" :key="'action-' + seatIdx")
                              v-list-item-title.text-sm.basic800--text {{ seat.name }}
                        template(v-else)
                          v-list-item
                            v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
              //- serving
              template(v-else-if="registration.status === 'serving'")
                v-card.round-xl.ml-4.pl-3.pr-1(outlined height="36" style="background-color: var(--v-yellow100);")
                  div.d-flex.align-center.fill-height
                    div.text-sm.font-bold.danger500--text.mr-3(v-if="registration.urgent") Urgent
                    div.text-sm.font-semibold.yellow600--text {{ `${getStatus(registration)}: ${getServingDuration(registration)}` }}
                    y-button.ml-3(label="Put on hold" :loading="isLoadingPutOnHold" :disabled="isLoadingCompleteConsultation" outlined color="neutral400" text-color="neutral700" background-color="var(--v-neutral100)" size="xxs" height="24" unset-min-width button-class="px-2" @click="onPutOnHold")

          div.parent-scroll(:style="{ '--left-panel-height': leftPanelHeight }")
            div.child-scroll(:style="{ '--left-panel-height': leftPanelHeight, '--nve-right-margin': nveRightMargin }")
              //- patient info
              patient-info-section.mt-1
              v-card.round-xl.mt-3(outlined)
                div.pa-4
                  //- visit note
                  v-card.round-xl(outlined)
                    .neutral200.px-3.py-2
                      div.d-flex.align-center
                        div.text-xs.font-medium.neutral700--text {{ $t('visit_note') }}:
                        div.text-xs.neutral700--text.mx-3 {{ consultation.case || '-' }}
                        v-spacer
                        y-button.ml-1(label="Edit" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" @click="showVisitNoteDialog = true")
                  //- vital signs
                  vital-signs-section.mt-3(type="table")
                  //- consultation note
                  div.mt-3
                    v-card.round-xl.fill-height.overflow-hidden(:loading="isUpdatingNote" outlined style="border-color: var(--v-neutral400)")
                      .d-flex.flex-column
                        div(v-if="isDoctor && registration")
                          text-viewer(v-if="registration.status === 'completed' && !isServingDoctor" :height="textEditorHeight")
                          text-editor(v-else ref="froala" :height="textEditorHeight" :isLoading="isUpdatingNote" @onSave="updateNote(true)")
                        text-viewer(v-else :height="textEditorHeight")
                  //- diagnosis
                  v-row.mt-5(no-gutters)
                    v-col(cols="6")
                      div.text-xs.font-medium.neutral700--text Diagnosis
                      diagnosis-field-dropdown.mt-3(:parentDiagnosis="consultation.diagnoses" :leadPid="leadPid" attach :z-index="15" :readOnly="!isDoctor" @updated="onUpdateDiagnosis")
              //- dispense note
              v-card.round-xl.mt-3.pa-4(outlined)
                div.text-xs.font-medium.neutral700--text Dispense note
                v-textarea.round-xl.text-sm.mt-3(v-model="dispenseNote" :placeholder="!isDoctor ? 'No dispense note available' : 'Enter dispense note...'" outlined hide-details :loading="isUpdatingDispenseNote" rows="4" auto-grow :disabled="!isDoctor" @blur="updateDispenseNote(true)")
              //- Upcoming appointments
              template(v-if="Auth('component', '/admin/clinic/consultation#set_appointment')")
                v-card.round-xl.mt-3.pa-4(outlined)
                  appointment-section

        //- right panel
        v-col(cols="5" style="padding-left: 6px;")
          v-card.round-xl(outlined)
            patient-history-treatment-plan
          //- total & complete visitation
          v-card.round-xl.mt-3.pa-4(outlined)
            div.d-flex.align-center
              div.text-md.font-medium.neutral700-text Total
              div.text-xs.neutral700-text.ml-3(v-if="consultation.treatments.length") {{ $tc('item', consultation.treatments.length) }}
              v-spacer
              div.text-md.font-medium.neutral700-text {{ totalPrice | displayPrice }}
            template(v-if="registration")
              template(v-if="['waiting', 'on_hold'].includes(registration.status)")
                y-button.mt-3(:label="registration.status === 'waiting' ? 'Start consultation' : 'Resume consultation'" block :loading="isLoadingStartConsultation" :disabled="isLoadingCallPatientIn" @click="onStartConsultation")
              template(v-else-if="registration.status === 'serving'")
                y-button.mt-3(label="Complete consultation" block :loading="isLoadingCompleteConsultation" :disabled="isLoadingPutOnHold" @click="checkCompleteConsultation")
              template(v-else-if="['checkout', 'completed'].includes(registration.status)")
                y-button.mt-3(label="Consultation completed" block disabled)
            template(v-else)
              y-button.mt-3(label="Migrated data" block disabled)
  .dialogs
    visit-note-dialog(v-if="showVisitNoteDialog" :open.sync="showVisitNoteDialog")
    consultation-not-found-error-dialog(v-if="showErrorDialog" :open.sync="showErrorDialog")
    alert-confirmation-dialog(v-if="showAlert" :open.sync="showAlert" @save="saveChanges" @discard="onClickBack")
      template(#title)
        div.text-md.font-semibold.neutral700--text Changes detected
      template(#content)
        template(v-if="hasUnsavedTreatment")
          div.text-sm.font-medium.neutral700--text You have {{ unsavedTreatmentCount }} unsaved items in this consultation:
          v-card.round-xl.pa-3.mt-2(color="neutral200" flat)
            div.text-xs.font-medium.neutral500--text Treatment plan
            template(v-for="(treatment, idx) in treatmentForms")
              div.text-xs.font-semibold.neutral700--text.mt-2(:key="`tmfm-${idx}`") {{ treatment.name }}
            template(v-for="(state, idx) in treatmentEditingStates")
              div.text-xs.font-semibold.neutral700--text.mt-2(:key="`tmst-${idx}`") {{ state.name }}
          div.text-sm.neutral700--text.mt-4 This action cannot be undone. Choose “Continue editing” to complete your changes, or “Discard” to remove them.
        div.text-md.neutral700--text(v-else) Unsaved changes detected. Do you want to save or discard the changes?
      template(v-if="hasUnsavedTreatment" #actions)
        y-button.mr-3(:label="$t('cancel')" text color="neutral700" size="sm" button-class="px-0" @click="showAlert = false")
        v-spacer
        y-button(label="Discard" color="danger500" outlined size="sm" @click="discardTreatmentForms")
        y-button(label="Continue editing" size="sm" dark @click="showAlert = false")

</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import WaitingDropdown from '@/components/clinic/consultation/WaitingDropdown.vue'
import PatientInfoSection from '@/components/clinic/consultation/PatientInfoSection.vue'
import DiagnosisFieldDropdown from '@/components/clinic/consultation/diagnosis/DiagnosisFieldDropdown.vue'
import VisitNoteDialog from '@/components/clinic/consultation/VisitNoteDialog.vue'
import VitalSignsSection from '@/components/clinic/consultation/VitalSignsSection.vue'
import TextEditor from '@/components/clinic/consultation/editor/TextEditor.vue'
import TextViewer from '@/components/clinic/consultation/editor/TextViewer.vue'
import AppointmentSection from '@/components/clinic/consultation/AppointmentSection.vue'
import ConsultationNotFoundErrorDialog from '@/components/clinic/dialogs/ConsultationNotFoundErrorDialog.vue'
import PatientHistoryTreatmentPlan from '@/components/clinic/consultation/PatientHistoryTreatmentPlan.vue'
import AlertConfirmationDialog from '@/components/clinic/consultation/AlertConfirmationDialog.vue'
import bell from '@/assets/audio/bell_announcement.mp3'

export default {
  components: {
    WaitingDropdown, PatientInfoSection, DiagnosisFieldDropdown, VisitNoteDialog, VitalSignsSection, TextEditor,
    TextViewer, AppointmentSection, ConsultationNotFoundErrorDialog, PatientHistoryTreatmentPlan, AlertConfirmationDialog
  },
  data:() => ({
    isFetching: true,
    isLoadingCallPatientIn: false,
    isLoadingPutOnHold: false,
    isLoadingStartConsultation: false,
    isLoadingCompleteConsultation: false,
    isError: false,
    isUpdatingNote: false,
    isUpdatingDispenseNote: false,
    isNavigatingToCheckout: false,
    showErrorDialog: false,
    showAllergiesConditionDialog: false,
    showVisitNoteDialog: false,
    showAlert: false,
    timer: null,
    statTimer: null,
    currentTime: null,
    oriDispenseNote: null,
    oriNote: null,
    clickedBack: false,
    uploadedFileCount: 0,
    stats: {},
    seats: [],
  }),
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor',
      consultation: 'treatment/getConsultation',
      registration: 'treatment/getRegistration',
      patient: 'treatment/getPatient',
      leadPid: 'treatment/getLeadPid',
      treatmentForms: 'treatment/getTreatmentForms',
      treatmentEditingStates: 'treatment/getTreatmentEditingStates',
      displaySize: 'ui/getDisplaySize',
      scrollbarWidth: 'ui/getScrollbarWidth'
    }),
    dispenseNote: {
      get() {
        return this.registration?.checkout_note
      },
      set(val) {
        let note = { checkout_note: val }
        this.$store.commit('treatment/updateRegistration', note)
      }
    },
    totalPrice() {
      return this.consultation.treatments.reduce((agg, treatment) => {
        agg += treatment.quantity * treatment.price
        return agg
      }, 0)
    },
    hasChanges () {
      let changes = false
      if (JSON.stringify(this.oriNote) !== JSON.stringify(this.consultation.note)) {
        changes = true
      }
      if (this.registration && JSON.stringify(this.oriDispenseNote) !== JSON.stringify(this.registration.checkout_note)) {
        changes = true
      }
      return changes
    },
    hasUnsavedTreatment () {
      if (this.treatmentForms.length || this.treatmentEditingStates.length) {
        return true
      }
      return false
    },
    unsavedTreatmentCount () {
      return this.treatmentForms.length + this.treatmentEditingStates.length
    },
    screenHeight () {
      return window.innerHeight
    },
    textEditorHeight () {
      let height = 160
      switch (this.displaySize) {
        case 'xx-small':
          return `${height / 0.67}px`
        case 'x-small':
          return `${height / 0.75}px`
        case 'small':
          return `${height / 0.8}px`
        case 'medium':
          return `${height / 0.9}px`
        case 'large':
          return `${height}px`
        case 'x-large':
          return `${height / 1.1}px`
        default:
          return `${height}px`
      }
    },
    leftPanelHeight () {
      let minusHeight = 150 - 72
      switch (this.displaySize) {
        case 'xx-small':
          return `${(this.screenHeight / 0.67) - minusHeight}px`
        case 'x-small':
          return `${(this.screenHeight / 0.75) - minusHeight}px`
        case 'small':
          return `${(this.screenHeight / 0.8) - minusHeight}px`
        case 'medium':
          return `${(this.screenHeight / 0.9) - minusHeight}px`
        case 'large':
          return `${(this.screenHeight) - minusHeight}px`
        case 'x-large':
          return `${(this.screenHeight / 1.1) - minusHeight}px`
        default:
          return `${(this.screenHeight) - minusHeight}px`
      }
    },
    nveRightMargin () {
      return `${(-10 - this.scrollbarWidth)}px`
    },
    isServingDoctor () {
      if (!this.isDoctor) {
        return false
      }
      if (this.member.provider === this.consultation.provider) {
        return true
      }
      return false
    }
  },
  watch: {
    stats(val, oldVal) {
      if (oldVal && this.isDoctor) {
        if (val.waiting > oldVal.waiting) {
          this.playAudio();
        }
      }
    },
  },
  created () {
    this.setData()
  },
  mounted () {
    this.fetchData()
    this.setPopState()
  },
  beforeDestroy () {
    let pid = this.leadPid
    this.clearTimer()
    this.$store.commit('treatment/resetTreatment')
    if (this.isNavigatingToCheckout) {
      this.$store.commit('treatment/setLeadPid', pid)
    }
    window.removeEventListener('popstate', this.handlePopState)
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.updateTime()
      this.timer = setInterval(this.updateTime, 1000);
      this.statTimer = setInterval(this.fetchStats, 12000);
      this.$store.commit('treatment/setLeadPid', this.$route.params.pid)
    },
    setPopState () {
      history.pushState(null, document.title, window.location.href)
      window.addEventListener('popstate', this.handlePopState)
    },
    setOriginalNote () {
      this.oriNote = this.consultation.note
    },
    setOriginalDispenseNote () {
      if (!this.registration) {
        return
      }
      this.oriDispenseNote = this.registration.checkout_note
    },
    async fetchData () {
      try {
        this.isFetching = true
        let allFetching = [this.fetchConsultation(), this.fetchUploadedFiles(), this.fetchAvailableSeats()]
        await Promise.all([...allFetching])
        this.isFetching = false
      } catch (error) {
        this.isError = true
        const { config, status } = error.response
        const consultationEndpoint = config.url.includes('/consultations')
        if (consultationEndpoint && status === 404) {
          this.showErrorDialog = true
          return
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.$nextTick(() => {
          this.setOriginalNote()
          this.setOriginalDispenseNote()
        })
      }
    },
    fetchConsultation () {
      return this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`)
        .then(resp => {
          this.$store.commit('treatment/setConsultation', resp.data)
        })
    },
    fetchUploadedFiles () {
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}/attachments`, { page_size: 1 })
        .then(resp => {
          this.uploadedFileCount = resp.data.count
        })
    },
    fetchAvailableSeats () {
      this.isLoadingCallPatientIn = true
      let params = {
        status: 'active',
        type: 'room',
        fields: 'id,name',
        page_size: -1
      }
      return this.$axios.get(`/clinics/${this.company.slug}/facilities`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.seats = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingCallPatientIn = false
        })
    },
    fetchStats() {
      this.$axios.get(`/clinics/${this.company.slug}/registrations/stats`, { params: { codes: "1,2" } })
        .then((resp) => {
          if (resp.data && resp.data.stats) {
            this.stats = resp.data.stats
          }
        })
        .catch(() => {});
    },
    checkClickBack () {
      if (this.hasChanges || this.hasUnsavedTreatment) {
        this.showAlert = true
        this.clickedBack = true
        return
      }
      this.onClickBack()
    },
    checkCompleteConsultation () {
      this.clickedBack = false
      if (this.hasUnsavedTreatment) {
        this.showAlert = true
        return
      }
      this.onCompleteConsultation()
    },
    onClickBack () {
      this.showAlert = false
      this.clickedBack = false
      let route = { name: 'ClinicConsultation' }
      if (this.$route.params && this.$route.params.origin) {
        if (this.$route.params.origin === 'ClinicCheckout') {
          this.isNavigatingToCheckout = true
        }
        route = { name: this.$route.params.origin }
      }
      if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
        route = { ...route, ...this.$route.params.data }
      }
      return this.$router.push(route).catch(() => {})
    },
    discardTreatmentForms () {
      this.showAlert = false
      if (this.clickedBack) {
        this.onClickBack()
      } else {
        this.$store.commit('treatment/resetTreatmentForms')
        this.$store.commit('treatment/resetTreatmentEditingStates')
      }
    },
    updateFileCount (count) {
      this.uploadedFileCount = count
    },
    updateTime() {
      this.currentTime = new Date().toISOString()
    },
    getStatus(item) {
      const text = item.status.replace(/_/g, ' ')
      return this.$options.filters.capitalize(text)
    },
    getWaitingDuration(item) {
      const end = DateTime.fromISO(this.currentTime)
      const start = DateTime.fromISO(item.waiting_at)

      let diff = end.diff(start, ['hours', 'minutes']);
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      if (hours) {
        return `${hours} ${this.$options.filters.pluralize('hour', { count:hours })}  ${minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
      }
      return `${minutes < 1 ? '<1' : minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
    },
    getServingDuration(item) {
      const end = DateTime.fromISO(this.currentTime)
      const start = DateTime.fromISO(item.serving_at)

      let diff = end.diff(start, ['hours', 'minutes', 'seconds']);
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      let seconds = Math.floor(diff.toObject().seconds)
      if (seconds <= 0) {
        return '00:00'
      }
      let hoursText = hours < 10 ? `0${hours}` : `${hours}`
      let minutesText = minutes < 10 ? `0${minutes}` : `${minutes}`
      let secondsText = seconds < 10 ? `0${seconds}` : `${seconds}`
      if (hours) {
        return `${hoursText}:${minutesText}:${secondsText}` 
      }
      return `${minutesText}:${secondsText}`
    },
    clearTimer() {
      clearInterval(this.timer)
      clearInterval(this.statTimer)
    },
    onCallPatientIn(seat) {
      this.isLoadingCallPatientIn = true
      let payload = {
        seat: seat?.id,
        status: 'serving'
      }
      if (this.member.roles.includes('Agent')) {
        payload.provider = this.member.provider
      }
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, payload)
        .then(resp => {
          let data = {
            status: resp.data.status,
            serving_at: resp.data.serving_at,
            waiting_at: resp.data.waiting_at,
            // on_hold_at: resp.data.on_hold_at,
          }
          this.$store.commit('treatment/updateRegistration', data)
          this.updateNote()
          this.updateDispenseNote()
        })
        .catch(error => {
          const errorData = error.response?.data?.detail
          if (errorData && errorData.includes('currently occupied')) {
            this.showMessage(['error', `${seat.name} is currently occupied`])
          } else {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.isLoadingCallPatientIn = false
        })
    },
    onStartConsultation () {
      this.isLoadingStartConsultation = true
      let payload = {
        status: 'serving'
      }
      if (this.member.roles.includes('Agent')) {
        payload.provider = this.member.provider
      }
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, payload)
        .then(resp => {
          let data = {
            status: resp.data.status,
            serving_at: resp.data.serving_at,
            waiting_at: resp.data.waiting_at,
            // on_hold_at: resp.data.on_hold_at,
          }
          this.$store.commit('treatment/updateRegistration', data)
          this.updateNote()
          this.updateDispenseNote()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingStartConsultation = false
        })
    },
    onPutOnHold() {
      this.isLoadingPutOnHold = true
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { status: 'on_hold' })
        .then(resp => {
          let data = {
            status: resp.data.status,
            serving_at: resp.data.serving_at,
            waiting_at: resp.data.waiting_at,
            // on_hold_at: resp.data.on_hold_at,
          }
          this.$store.commit('treatment/updateRegistration', data)
          this.updateNote()
          this.updateDispenseNote()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoadingPutOnHold = false
        })
    },
    async onCompleteConsultation() {
      this.isLoadingCompleteConsultation = true
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/registrations/${this.leadPid}`, { status: 'checkout' })
        const itemCount = this.consultation.treatments?.length || 0
        const totalAmount = this.$options.filters.displayPrice(this.totalPrice, this.$currency)
        this.createActivityLog('complete_consultation', `Consultation completed with ${itemCount} items worth ${totalAmount}`)
        await this.updateNote()
        await this.updateDispenseNote()
        let route = { name: 'ClinicConsultation'}
        if (this.$route.params && this.$route.params.data && Object.keys(this.$route.params.data).length) {
          route = { ...route, ...this.$route.params.data }
        }
        this.$router.push(route)
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingCompleteConsultation = false
      }
    },
    onUpdateDiagnosis(diagnoses) {
      this.$store.commit('treatment/updateConsultation', { diagnoses: diagnoses })
    },
    updateNote (showMessage = false) {
      if (!this.isDoctor || this.consultation.note === null) {
        return
      }
      let payload = { note: this.consultation.note }
      this.isUpdatingNote = true
      const endpoint = `/clinics/${this.company.slug}/consultations/${this.leadPid}`
      this.$axios.patch(endpoint, payload)
        .then((resp) => {
          this.$store.commit('treatment/setConsultation', resp.data)
          this.setOriginalNote()
          if (showMessage) {
            this.showMessage(['success', this.$t('success_message.update_successful')])
            this.updateDispenseNote()
          }
          let activity = `Update consultation note (${this.leadPid})`
          this.updateActivityLog(endpoint, payload, resp, 'PATCH', activity)
        })
        .catch(() => {
          if (showMessage) {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.isUpdatingNote = false
        })
    },
    updateDispenseNote (showMessage = false) {
      if (!this.registration) {
        return
      }
      if (!this.isDoctor || this.registration.checkout_note === null) {
        return
      }
      let payload = { checkout_note: this.dispenseNote }
      this.isUpdatingDispenseNote = true
      const endpoint = `/clinics/${this.company.slug}/registrations/${this.leadPid}`
      this.$axios.patch(endpoint, payload)
        .then((resp) => {
          this.setOriginalDispenseNote()
          if (showMessage) {
            this.showMessage(['success', this.$t('success_message.update_successful')])
          }
          let activity = `Update dispense note (${this.leadPid})`
          this.updateActivityLog(endpoint, payload, resp, 'PATCH', activity)
        })
        .catch(() => {
          if (showMessage) {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.isUpdatingDispenseNote = false
        })
    },
    async saveChanges () {
      await this.updateNote()
      await this.updateDispenseNote()
      await this.onClickBack()
    },
    updateActivityLog (endpoint, payload, response, method, activity) {
      let data = {
        company: this.company.id,
        object_id: this.leadPid,
        object_ref: 'consultation',
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'consultation',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
    playAudio() {
      this.audio = new Audio(bell)
      this.audio.play()
    },
    getMemberRole (roles) {
      if (roles.length > 2) {
        return `${this.getClinicRole(roles[0])} and ${roles.length - 1} more`
      } else if (roles.length > 1) {
        return roles.map(m => this.getClinicRole(m)).join(', ')
      } else {
        return this.getClinicRole(roles[0])
      }
    },
    getClinicRole (role) {
      switch (role) {
        case 'Admin':
          return 'Admin'
        case 'Salesperson':
          return 'Clinic Assistance'
        case 'Agent':
          return 'Doctor'
        case 'Bookkeeper':
          return 'Finance'
        default:
          return role
      }
    },
    redirectTo (route) {
      this.$router.push(route).catch(()=>{})
    },
    logoutUrl () {
      let link = '/signout'
      if (this.member.username) {
        link += '?username=true'
      }
      return link
    },
    handlePopState() {
      this.checkClickBack()
      history.pushState(null, document.title, window.location.href)
    },
  },
}
</script>

<style lang="scss" scoped>
.fullscreen {
  height: 100vh;
  position: relative;
  margin-top: -72px;
  z-index: 12;
}
.overflow-hidden {
  overflow: hidden !important;
}
.scrollable {
  overflow-y: auto;
}
.parent-scroll {
  overflow: hidden;
  height: var(--left-panel-height) !important;
}
.child-scroll {
  overflow: auto;
  height: var(--left-panel-height) !important;
  margin-right: var(--nve-right-margin);
  padding-right: 10px;
}
</style>

<i18n>
{
  "en": {
    "item" : "1 item | {n} items"
  }
}
</i18n>